import { ColorPricingSettings, ProductCollectionType } from '../types';

export interface CakeColor {
  name: string;
  value: string;
  border: string;
}

export interface ColorPriceContext {
  collection?: ProductCollectionType;
  productId?: string;
  settings?: ColorPricingSettings | null;
}

export const CAKE_COLORS: CakeColor[] = [
  { name: 'White', value: '#FFFFFF', border: '#E5E5E5' },
  { name: 'Ivory', value: '#FFFFF0', border: '#E5E5E5' },
  { name: 'Nude', value: '#F5E6D3', border: '#E5E5E5' },
  { name: 'Black', value: '#000000', border: '#E5E5E5' },
  { name: 'Red', value: '#FF0000', border: '#E5E5E5' },
  { name: 'Yellow', value: '#FFFF00', border: '#E5E5E5' },
  { name: 'Light Pink', value: '#FFB6C1', border: '#E5E5E5' },
  { name: 'Deep Pink', value: '#FF1493', border: '#E5E5E5' },
  { name: 'Orange', value: '#FFA500', border: '#E5E5E5' },
  { name: 'Light Green', value: '#90EE90', border: '#E5E5E5' },
  { name: 'Dark Green', value: '#006400', border: '#E5E5E5' },
  { name: 'Light Blue', value: '#ADD8E6', border: '#E5E5E5' },
  { name: 'Dark Blue', value: '#00008B', border: '#E5E5E5' },
  { name: 'Cream', value: '#FFF8DC', border: '#E5E5E5' },
  { name: 'Brown', value: '#8B4513', border: '#E5E5E5' },
  { name: 'Light Purple', value: '#DDA0DD', border: '#E5E5E5' },
  { name: 'Dark Purple', value: '#800080', border: '#E5E5E5' }
];

export const createDefaultColorPricingSettings = (): ColorPricingSettings => ({
  id: 'default',
  globalPrices: { White: 5 },
  collectionPrices: {
    'Create Your Own Cake': { White: 20 }
  },
  productPrices: {},
  createdAt: new Date(),
  updatedAt: new Date()
});

const isStoredPrice = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const getGlobalColorPrice = (
  colorName: string,
  settings?: ColorPricingSettings | null
): number => {
  const source = settings ?? createDefaultColorPricingSettings();
  const stored = source.globalPrices?.[colorName];
  return isStoredPrice(stored) ? stored : 0;
};

export const getCollectionColorPrice = (
  colorName: string,
  collection?: ProductCollectionType,
  settings?: ColorPricingSettings | null
): number => {
  const source = settings ?? createDefaultColorPricingSettings();
  if (collection) {
    const stored = source.collectionPrices?.[collection]?.[colorName];
    if (isStoredPrice(stored)) return stored;
  }
  return getGlobalColorPrice(colorName, source);
};

export const getBaseColorAdditionalPrice = (
  colorName: string | undefined,
  context: ColorPriceContext = {}
): number => {
  if (!colorName) return 0;
  const source = context.settings ?? createDefaultColorPricingSettings();

  if (context.collection && context.productId) {
    const stored =
      source.productPrices?.[context.collection]?.[colorName]?.[context.productId];
    if (isStoredPrice(stored)) return stored;
  }

  return getCollectionColorPrice(colorName, context.collection, source);
};

export const parseColorPriceInput = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return value;
};

export const formatColorPriceInput = (value: number | undefined): string =>
  isStoredPrice(value) ? String(value) : '';
